import type { DefLookup } from "../catalog";
import { isAcTerminalId } from "../ac";
import { hasMissingConsumerCurrent } from "../component-current";
import { effectiveTerminals } from "../effective";
import { checkImplausibleAmps } from "../custom-component-plausibility";
import { diagnosticLabel } from "../i18n/diagnostic-label";
import type { Issue, System } from "../types";

/** Scoped to `definitionId === "custom"`: catalog entries are held to this by
 *  `catalog-invariants.test.ts`, and a node the user edits becomes custom anyway. */

/** An `ampDraw` / `ampSurge` pair that can't describe a real part. A warning,
 *  never a block — the user may know something the app doesn't. */
export function detectImplausibleCustomAmps(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const issues: Issue[] = [];
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    if (node.definitionId !== "custom" || !node.customDefinition) continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    const finding = checkImplausibleAmps(def);
    if (!finding) continue;

    const label =
      node.label ?? def.shortName ?? def.name ?? diagnosticLabel("diag.label.component");
    const base: Pick<Issue, "code" | "severity" | "nodeIds"> = {
      code: "custom-component-implausible-amps",
      severity: "warning",
      nodeIds: [node.id],
    };

    if (finding.reason === "surge-below-draw") {
      issues.push({
        ...base,
        title: {
          key: "diag.custom-component-implausible-amps.surge-below-draw.title",
          vars: { label },
        },
        message: {
          key: "diag.custom-component-implausible-amps.surge-below-draw.message",
          vars: {
            label,
            ampDraw: finding.ampDraw,
            ampSurge: finding.ampSurge,
          },
        },
        fix: {
          key: "diag.custom-component-implausible-amps.surge-below-draw.fix",
        },
      });
    } else {
      issues.push({
        ...base,
        title: {
          key: "diag.custom-component-implausible-amps.engine-surge.title",
          vars: {
            label,
            ampDraw: finding.ampDraw,
            ampSurge: finding.ampSurge,
          },
        },
        message: {
          key: "diag.custom-component-implausible-amps.engine-surge.message",
          vars: {
            label,
            ampDraw: finding.ampDraw,
            ampSurge: finding.ampSurge,
            ratio: Math.round(finding.ratio),
          },
        },
        fix: {
          key: "diag.custom-component-implausible-amps.engine-surge.fix",
        },
      });
    }
  }
  return issues;
}

/** One issue for the whole system, not one per component; `nodeIds` carries every
 *  offender so clicking it selects them all. Self-clearing: `customizeComponent`
 *  drops a field's marker once the user edits the value. */
export function detectAiEstimatedSpecs(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  const nodeIds: string[] = [];
  let fieldCount = 0;

  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    if (node.definitionId !== "custom" || !node.customDefinition) continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def || def.source !== "ai") continue;
    const fields = def.aiEstimatedFields?.length ?? 0;
    if (fields === 0) continue;
    nodeIds.push(node.id);
    fieldCount += fields;
  }

  if (nodeIds.length === 0) return [];

  return [
    {
      code: "custom-component-ai-estimated",
      severity: "info",
      nodeIds,
      title: {
        key: "diag.custom-component-ai-estimated.title",
        vars: { count: nodeIds.length },
      },
      message: {
        key: "diag.custom-component-ai-estimated.message",
        vars: { count: nodeIds.length, fieldCount },
      },
      fix: { key: "diag.custom-component-ai-estimated.fix" },
    },
  ];
}

/** Missing demand is distinct from a declared zero: neither the current
 * subtotal nor its power/runtime estimate includes these connected consuming loads/monitors. */
export function detectMissingLoadCurrent(system: System, defLookup: DefLookup): Issue[] {
  const wired = new Map<string, Set<string>>();
  for (const cable of system.cables) {
    for (const [nodeId, terminalId] of [[cable.fromNodeId, cable.fromTerminalId], [cable.toNodeId, cable.toTerminalId]]) {
      if (!wired.has(nodeId)) wired.set(nodeId, new Set());
      wired.get(nodeId)!.add(terminalId);
    }
  }
  const nodeIds = system.nodes.filter(node => {
    if (node.kind !== "component") return false;
    const def = defLookup(node.definitionId, node.customDefinition);
    return def != null && hasMissingConsumerCurrent(def, wired.get(node.id)) &&
      effectiveTerminals(def, node).some(terminal => wired.get(node.id)?.has(terminal.id) &&
        !isAcTerminalId(terminal.id) &&
        ["positive", "negative", "ground"].includes(terminal.polarity));
  }).map(node => node.id).sort();
  if (nodeIds.length === 0) return [];
  return [{
    code: "load-current-unverified", severity: "warning", nodeIds,
    title: { key: "diag.load-current-unverified.title" },
    message: { key: "diag.load-current-unverified.message", vars: { count: nodeIds.length } },
    fix: { key: "diag.load-current-unverified.fix" },
  }];
}

export function runDefinitionDiagnostics(
  system: System,
  defLookup: DefLookup,
): Issue[] {
  return [
    ...detectRequestedComponentPlaceholders(system, defLookup),
    ...detectMissingLoadCurrent(system, defLookup),
    ...detectImplausibleCustomAmps(system, defLookup),
    ...detectAiEstimatedSpecs(system, defLookup),
  ];
}

/** Keep placeholder uncertainty visible even before wiring or after numeric
 * edits: a typed rating does not verify the requested model's pin layout. */
export function detectRequestedComponentPlaceholders(system: System, defLookup: DefLookup): Issue[] {
  const nodeIds = system.nodes.filter(node => {
    if (node.kind !== "component" || node.definitionId !== "custom") return false;
    const requested = defLookup(node.definitionId, node.customDefinition)?.requestedModel;
    return typeof requested === "string" && requested.trim().length > 0;
  }).map(node => node.id).sort();
  if (nodeIds.length === 0) return [];
  return [{
    code: "requested-component-unverified", severity: "warning", nodeIds,
    title: { key: "diag.requested-component-unverified.title" },
    message: { key: "diag.requested-component-unverified.message", vars: { count: nodeIds.length } },
    fix: { key: "diag.requested-component-unverified.fix" },
  }];
}
